import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { ScanLine, Users, Search, Bell, User as UserIcon, WifiOff, Wifi, ArrowLeftRight, LogOut, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { syncQueue, getQueue, isOnline as isOnlineFn } from "@/lib/offline";
import { usePWAInstall } from "@/lib/usePWAInstall";
import { toast } from "sonner";

const TABS = [
  { to: "/m",               label: "Scan",     icon: ScanLine, end: true },
  { to: "/m/class",         label: "Class",    icon: Users },
  { to: "/m/search",        label: "Search",   icon: Search },
  { to: "/m/notifications", label: "Alerts",   icon: Bell },
  { to: "/m/profile",       label: "Profile",  icon: UserIcon },
];

const MobileLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [online, setOnline] = useState(isOnlineFn());
  const [queueCount, setQueueCount] = useState(getQueue().length);
  const { canInstall, installed, promptInstall } = usePWAInstall();

  useEffect(() => {
    const setStatus = () => setOnline(isOnlineFn());
    const refresh = () => setQueueCount(getQueue().length);
    window.addEventListener("online", () => { setStatus(); syncQueue().then(refresh); });
    window.addEventListener("offline", setStatus);
    const i = setInterval(refresh, 4000);
    return () => clearInterval(i);
  }, []);

  const doSync = async () => {
    const r = await syncQueue();
    setQueueCount(getQueue().length);
    toast.success(`Synced ${r.synced} · ${r.remaining} pending`);
  };

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-[100dvh] bg-[#FDFBF7] flex flex-col" data-testid="mobile-shell">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-[#1B2C5A] text-white" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="flex items-center gap-3 px-4 h-14">
          <img src="/dfa-logo.svg" alt="DFA" className="w-9 h-9 rounded-full bg-white p-0.5" />
          <div className="flex-1 leading-tight min-w-0">
            <div className="font-display text-sm truncate">{user?.name || "Teacher"}</div>
            <div className="overline text-[#9DCD3F] truncate">DFA Teacher App</div>
          </div>
          <button
            onClick={online ? doSync : undefined}
            className={"relative flex items-center gap-1 px-2.5 py-1 rounded-full text-xs " + (online ? "bg-[#9DCD3F]/20 text-[#9DCD3F]" : "bg-amber-500/20 text-amber-200")}
            data-testid="online-pill"
          >
            {online ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
            {online ? "Online" : "Offline"}
            {queueCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#9DCD3F] text-[#1B2C5A] font-medium text-[10px]" data-testid="queue-badge">
                {queueCount}
              </span>
            )}
          </button>
        </div>
        {/* Install prompt */}
        {canInstall && !installed && (
          <button onClick={promptInstall} className="w-full bg-[#9DCD3F] text-[#1B2C5A] text-xs py-1.5 font-medium flex items-center justify-center gap-1.5" data-testid="install-prompt">
            <Download className="w-3.5 h-3.5" /> Install DFA Teacher App
          </button>
        )}
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto pb-24" data-testid="mobile-content">
        <Outlet />
      </main>

      {/* Bottom tabs */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-[#E2E8F0]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="mobile-tabs"
      >
        <div className="grid grid-cols-5 max-w-md mx-auto">
          {TABS.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              className={({ isActive }) =>
                "flex flex-col items-center gap-0.5 py-2.5 text-[11px] transition-colors " +
                (isActive ? "text-[#1B2C5A]" : "text-[#475569]")
              }
              data-testid={`tab-${t.label.toLowerCase()}`}
            >
              {({ isActive }) => (
                <>
                  <div className={"w-7 h-7 rounded-full grid place-items-center " + (isActive ? "bg-[#9DCD3F]/20" : "")}>
                    <t.icon className={"w-4 h-4 " + (isActive ? "text-[#1B2C5A]" : "")} />
                  </div>
                  <span className={isActive ? "font-semibold" : ""}>{t.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Floating actions in profile when needed */}
      <button onClick={doLogout} className="hidden" data-testid="mobile-logout-hidden" />
    </div>
  );
};

export default MobileLayout;
